import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowLeft, ArrowRight, Users, Target, Lightbulb, Award, Zap, Shield, Clock, TrendingUp, Star, Quote, CheckCircle, Brain, Menu, X } from 'lucide-react';
import { useAuth, AuthModal, UserMenu } from '../auth/AuthComponents';
import { apiRequest } from '../../utils/api';
import { useNavigate } from 'react-router-dom';

const AboutPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [backendStats, setBackendStats] = useState({ totalContracts: 0, totalSOPs: 0, totalCVs: 0 });
  const [loadingBackendStats, setLoadingBackendStats] = useState(true);

  const slides = [
    { title: "About", key: "about" },
    { title: "Benefits", key: "benefits" },
    // { title: "Testimonials", key: "testimonials" }, // hide until we have real users
  ];

  const values = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Contract Analysis & Revamp",
      description: "Upload any contract and get a structured review in minutes. Get flagged risks, unclear clauses, and practical suggestions that help you negotiate with confidence."
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "SOP Analysis & Revamp", 
      description: "Strengthen your statement of purpose with clear feedback on structure, tone, and fit. Know exactly what to improve before you submit."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "CV Analysis & Revamp",
      description: "Sharpen your CV for stronger first impressions. Get better wording, clearer impact, and tighter alignment with the roles you are targeting."
    }
  ];

  const benefits = [
    {
      icon: <Zap className="w-10 h-10" />,
      title: "Lightning Fast",
      description: "99% faster than traditional methods",
      stat: "30 seconds"
    },
    {
      icon: <Brain className="w-10 h-10" />,
      title: "AI-Powered",
      description: "Advanced machine learning precision",
      stat: "99.7% accuracy"
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: "Secure",
      description: "Enterprise-grade protection",
      stat: "Zero breaches"
    }
  ];

  const testimonials = [
    // {
    //   name: "Dr. Sarah Mitchell",
    //   role: "Senior Researcher",
    //   // company: "Stanford University",
    //   //image: "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=400&h=400&fit=crop&crop=face",
    //   rating: 5,
    //           text: "DocxsAi transformed how we analyze research proposals. What used to take days now takes minutes with remarkable accuracy."
    // },
    {
      name: "Emanuel Isaac", 
      role: "Accounting Student",
      company: "University of Strathclyde",
      //image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
      rating: 5,
      text: "This really helped me identify lapses in my SOP and provided me hints on how to improve it."
    },
    {
      name: "Tobi Adeyemi",
      role: "Legal Counsel", 
      // company: "GlobalLaw Partners",
      //image: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&h=400&fit=crop&crop=face",
      rating: 5,
      text: "Contract review is lightning fast. The risk assessment has helped catch issues before they become expensive problems."
    }
  ];

  const dynamicStats = [
    { number: backendStats.totalContracts.toLocaleString() + '+', label: 'Contracts Analyzed' },
    { number: backendStats.totalSOPs.toLocaleString() + '+', label: 'SOPs Enhanced' },
    { number: backendStats.totalCVs.toLocaleString() + '+', label: 'Resumes Optimized' }
  ];

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'ArrowLeft') {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
      } else if (e.key === 'ArrowRight') {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [slides.length]);

  // Auto-rotate testimonials
  useEffect(() => {
    if (currentSlide === 2) { // Only on testimonials slide
      const interval = setInterval(() => {
        setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [currentSlide, testimonials.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        className={`w-4 h-4 ${i < rating ? 'text-yellow-400 fill-current' : 'text-gray-400'}`} 
      />
    ));
  };

  // Add the keyframe animations
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes float {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        33% { transform: translate(10px, -15px) rotate(1deg); }
        66% { transform: translate(-5px, 5px) rotate(-1deg); }
      }
      @keyframes float2 {
        0%, 100% { transform: translate(0, 0) rotate(0deg); }
        50% { transform: translate(-20px, -20px) rotate(2deg); }
      }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(30px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(20px); }
        to { opacity: 1; transform: translateY(0); }
      }
      .animate-float { animation: float 8s ease-in-out infinite; }
      .animate-float2 { animation: float2 10s ease-in-out infinite; }
      .animate-slideUp { animation: slideUp 0.6s ease-out; }
      .animate-fadeIn { animation: fadeIn 0.8s ease-out; }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const renderAboutSlide = () => (
    <div className="max-w-6xl mx-auto text-center w-full animate-slideUp px-4 pt-2 sm:pt-4">
      <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--color-text)] mb-2 sm:mb-3 leading-tight">
        Optimizing Document{" "}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
          Processing with AI
        </span>
      </h1>
      <p className="text-base sm:text-lg md:text-xl text-[var(--color-text-muted)] mb-3 sm:mb-4 max-w-3xl mx-auto leading-relaxed">
        DocxsAi helps professionals transform complex documents into clear, actionable insights using AI, enabling faster, smarter decision-making with confidence.
      </p>
      {/* Values Grid */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3 md:gap-4 mt-6 sm:mt-8 md:mt-10">
        {values.map((value, index) => (
          <div
            key={index}
            className={index === 1 ? 'public-tilt-wrap' : undefined}
          >
            <div 
              className="elevated-card backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-[var(--color-surface-muted)] transition-all group h-full"
            >
            <div className="flex items-start gap-3 sm:gap-4">
              {/* Icon */}
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-[var(--color-primary-soft)] flex items-center justify-center transition-all">
                <div className="text-[var(--color-primary)] transition-colors">
                  {React.cloneElement(value.icon, { className: "w-5 h-5 sm:w-6 sm:h-6" })}
                </div>
              </div>
              {/* Content */}
              <div className="flex-grow min-w-0">
                <h3 className="text-base sm:text-lg font-bold text-[var(--color-text)] mb-1 group-hover:text-[var(--color-primary)] transition-colors text-left sm:text-left">
                  {value.title}
                </h3>
                <p className="text-[var(--color-text-muted)] text-xs sm:text-sm leading-relaxed group-hover:text-[var(--color-text-secondary)] transition-colors text-left sm:text-left">
                  {value.description}
                </p>
              </div>
            </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderBenefitsSlide = () => (
    <div className="max-w-6xl mx-auto text-center w-full animate-slideUp px-4 pt-2 sm:pt-4">
      <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-[var(--color-text)] mb-2 sm:mb-3 leading-tight">
        Why Choose{" "}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
          DocxsAi
        </span>
        ?
      </h1>
      <p className="text-base sm:text-lg md:text-xl text-[var(--color-text-muted)] mb-3 sm:mb-4 max-w-3xl mx-auto leading-relaxed">
        DocxsAi is your complete AI document workspace for generating, improving, reviewing, and securely signing documents with confidence.
      </p>
      {/* Benefits Grid */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-3 md:gap-4 mt-6 sm:mt-8 md:mt-10">
        {benefits.map((benefit, index) => (
          <div 
            key={index}
            className="elevated-card backdrop-blur-sm rounded-xl sm:rounded-2xl p-3 sm:p-4 hover:bg-[var(--color-surface-muted)] transition-all group"
          >
            <div className="flex items-start gap-3 sm:gap-4">
              {/* Icon */}
              <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl bg-[var(--color-primary-soft)] flex items-center justify-center transition-all">
                <div className="text-[var(--color-primary)] transition-colors">
                  {React.cloneElement(benefit.icon, { className: "w-5 h-5 sm:w-6 sm:h-6" })}
                </div>
              </div>
              {/* Content */}
              <div className="flex-grow min-w-0 text-left">
                <h3 className="text-base sm:text-lg font-bold text-[var(--color-text)] mb-1 group-hover:text-[var(--color-primary)] transition-colors">
                  {benefit.title}
                </h3>
                <p className="text-[var(--color-text-muted)] text-xs sm:text-sm mb-1 sm:mb-2 leading-relaxed group-hover:text-[var(--color-text-secondary)] transition-colors">
                  {benefit.description}
                </p>
                <div className="inline-flex items-center px-2 py-1 bg-[var(--color-primary-soft)] rounded-full">
                  <span className="text-xs font-medium text-[var(--color-primary)]">{benefit.stat}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* Quick Features */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
        {[
          "Instant Analysis",
          "99.7% Accuracy",
          "24/7 Available"
        ].map((feature, index) => (
          <div key={index} className="flex items-center justify-center space-x-2 p-2 sm:p-3 bg-[var(--color-surface-muted)] backdrop-blur-sm border border-[var(--color-border)] rounded-lg sm:rounded-xl">
            <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-[var(--color-success)] flex-shrink-0" />
            <span className="text-[var(--color-text)] text-xs sm:text-sm">{feature}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const renderTestimonialsSlide = () => (
    <div className="max-w-6xl mx-auto text-center w-full animate-slideUp px-4">
      <h1 className="text-xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 sm:mb-4 leading-tight">
        Trusted by{" "}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)]">
          Industry Leaders
        </span>
      </h1>
      <p className="text-base sm:text-lg md:text-xl text-[var(--color-text-muted)] mb-3 sm:mb-4 max-w-3xl mx-auto leading-relaxed">
        Don't just take our word for it. See what industry leaders and professionals 
        have to say about their DocxsAi experience.
      </p>
      {/* Two Column Layout: Testimonial & Stats - Mobile First */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2 lg:gap-8">
        {/* Featured Testimonial - Full width on mobile */}
        <div className="bg-[var(--color-surface)]/80 backdrop-blur-sm border border-[var(--color-border)] rounded-2xl sm:rounded-3xl p-4 sm:p-6">
          <div className="text-center mb-3 sm:mb-4">
            <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-[var(--color-primary)] mx-auto mb-2 sm:mb-3" />
            <p className="text-sm sm:text-lg text-[var(--color-text-secondary)] leading-relaxed italic mb-3 sm:mb-4">
              "{testimonials[currentTestimonial]?.text || ''}"
            </p>
          </div>
          
          <div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-3 sm:mb-4">
            {/* <img 
              src={testimonials[currentTestimonial].image} 
              alt={testimonials[currentTestimonial].name}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg sm:rounded-xl object-cover"
            /> */}
            <div className="text-left">
              <h4 className="text-[var(--color-text)] font-bold text-xs sm:text-sm">{testimonials[currentTestimonial]?.name || ''}</h4>
              <p className="text-[var(--color-primary)] font-medium text-xs">{testimonials[currentTestimonial]?.role || ''}</p>
              <div className="flex space-x-1">
                 {renderStars(testimonials[currentTestimonial]?.rating || 0)}
              </div>
              <p className="text-[var(--color-text-muted)] text-xs">{testimonials[currentTestimonial]?.company || ''}</p>
            </div>
          </div>
          
          {/* Testimonial indicators */}
          <div className="flex justify-center space-x-1.5 sm:space-x-2">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${
                  currentTestimonial === index 
                    ? 'bg-[var(--color-primary)]' 
                    : 'bg-[var(--color-border)] hover:bg-[var(--color-text-muted)]'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Stats Carousel - Full width on mobile */}
        <div className="bg-[var(--color-surface)]/80 backdrop-blur-sm border border-[var(--color-border)] rounded-2xl sm:rounded-3xl p-4 sm:p-6">
          <div className="text-center h-full flex flex-col justify-center">
            <div className="mb-3 sm:mb-4">
              <div className="text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--color-text)] mb-1 sm:mb-2">
                {loadingBackendStats ? '...' : backendStats.totalContracts.toLocaleString()}+
              </div>
              <div className="text-sm sm:text-lg text-[var(--color-text-muted)] mb-3 sm:mb-4">
                {backendStats.totalContracts > 0 ? backendStats.totalContracts.toLocaleString() : 'No contracts analyzed yet'}
              </div>
            </div>
            
            {/* Additional stats preview */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-3 sm:mb-4">
              {dynamicStats.map((stat, index) => (
                <div 
                  key={index}
                  className={`p-2 sm:p-3 rounded-lg sm:rounded-xl transition-all cursor-pointer ${
                    currentTestimonial === index 
                      ? 'bg-[var(--color-primary-soft)] border border-[var(--color-primary)]/40' 
                      : 'bg-[var(--color-surface-muted)] hover:opacity-90'
                  }`}
                  onClick={() => setCurrentTestimonial(index)}
                >
                  <div className="text-sm sm:text-lg font-bold text-[var(--color-text)]">{loadingBackendStats ? '...' : stat.number}</div>
                  <div className="text-xs text-[var(--color-text-muted)]">{stat.label}</div>
                </div>
              ))}
            </div>
            
            {/* Stats indicators */}
            <div className="flex justify-center space-x-1.5 sm:space-x-2">
              {dynamicStats.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full transition-all ${
                    currentTestimonial === index 
                      ? 'bg-[var(--color-primary)]' 
                      : 'bg-[var(--color-border)] hover:bg-[var(--color-text-muted)]'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderCurrentSlide = () => {
    switch (currentSlide) {
      case 0: return renderAboutSlide();
      case 1: return renderBenefitsSlide();
      // case 2: return renderTestimonialsSlide(); // commented out — no public testimonials yet
      default: return renderAboutSlide();
    }
  };

  const navLinks = [
    { name: 'Home', action: () => navigate('/home'), current: false },
    { name: 'About', href: '#', current: true },
    { name: 'Pricing', action: () => navigate('/pricing'), current: false },
  ];

  useEffect(() => {
    const fetchStats = async () => {
      setLoadingBackendStats(true);
      try {
        // Use the correct endpoint for global analytics
        const data = await apiRequest('analytics/global');
        setBackendStats({
          totalContracts: data.totalContracts || 0,
          totalSOPs: data.totalSOPs || 0,
          totalCVs: data.totalCVs || 0
        });
      } catch (err) {
        setBackendStats({ totalContracts: 0, totalSOPs: 0, totalCVs: 0 });
      } finally {
        setLoadingBackendStats(false);
      }
    };
    fetchStats();
  }, []);

  return (
    <>
      <Helmet>
        <title>About – DocxsAi</title>
        <meta name="description" content="Learn how DocxsAi helps you generate and analyze Contracts, CVs, and SOPs with AI—professional drafting, legal precision, and clean PDF/DOCX outputs." />
        <link rel="canonical" href="https://docxsai.com/about" />
      </Helmet>
      <div className="flex-1 flex flex-col public-page-glow relative overflow-hidden">
        {/* Floating Elements */}
        <div className="absolute top-10 left-4 w-32 h-32 sm:top-20 sm:left-20 sm:w-80 sm:h-80 bg-[var(--color-primary-soft)] rounded-full blur-3xl animate-float"></div>
        <div className="absolute top-20 right-4 w-40 h-40 sm:top-40 sm:right-20 sm:w-96 sm:h-96 bg-gradient-to-br from-[var(--color-gradient-from)]/10 to-[var(--color-gradient-to)]/10 rounded-full blur-3xl animate-float2"></div>
        <div className="absolute bottom-16 left-6 w-24 h-24 sm:bottom-32 sm:left-32 sm:w-64 sm:h-64 bg-[var(--color-primary-soft)] rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-10 right-6 w-28 h-28 sm:bottom-20 sm:right-40 sm:w-72 sm:h-72 bg-gradient-to-br from-[var(--color-gradient-to)]/10 to-[var(--color-gradient-from)]/10 rounded-full blur-3xl animate-float2"></div>
        {/* Main Content */}
        <main className="relative z-10 px-2 sm:px-6 pt-28 sm:pt-32 pb-2 sm:pb-3">
          {renderCurrentSlide()}
        </main>
      {/* Slide Navigation */}
      <div className="flex justify-center items-center py-2 border-b border-[var(--color-border)] shrink-0 mt-auto">
        <button 
          onClick={prevSlide}
          className="p-1.5 sm:p-2 bg-[var(--color-surface-muted)] hover:opacity-90 rounded-lg sm:rounded-xl transition-all mr-3 sm:mr-4"
        >
          <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--color-text)]" />
        </button>
        <div className="flex space-x-2 sm:space-x-3">
          {slides.map((slide, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl transition-all text-xs sm:text-sm font-medium ${
                currentSlide === index 
                  ? 'bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] text-[var(--color-text-inverse)]' 
                  : 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {slide.title}
            </button>
          ))}
        </div>
        <button 
          onClick={nextSlide}
          className="p-1.5 sm:p-2 bg-[var(--color-surface-muted)] hover:opacity-90 rounded-lg sm:rounded-xl transition-all ml-3 sm:ml-4"
        >
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--color-text)]" />
        </button>
      </div>
      {/* Keyboard hint */}
      <div className="absolute bottom-3 sm:bottom-4 right-4 sm:right-6 text-xs text-[var(--color-text-muted)] hidden md:block">
        Use ← → keys to navigate
      </div>
      </div>
      
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
      />
    </>
  );
};

export default AboutPage; 